import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Spinner, Button, Modal } from 'react-bootstrap';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { toast } from 'react-toastify';
import { api } from 'api';
import { routes } from 'common';
import { FaCalendarAlt, FaRedo } from 'react-icons/fa';
import {
  CALENDAR_LABELS,
  CALENDAR_OPTIONS,
  CALENDAR_STATUS,
  CALENDAR_VIEWS,
  CALENDAR_MESSAGES,
  CALENDAR_TOOLTIP,
  CALENDAR_MODAL,
} from './constants';
import SingleSelect from 'components/singleSelect/SingleSelect';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './_calendar.scss';
import {APPROVAL_STATUS} from 'common/constants';

const localizer = momentLocalizer(moment);

// Distinct colors for each ground field (cycle if more fields than colors)
const FIELD_COLOR_PALETTE = [
  '#2563eb', '#16a34a', '#ca8a04', '#dc2626', '#9333ea',
  '#0891b2', '#ea580c', '#4f46e5', '#059669', '#be185d',
  '#0d9488', '#c026d3', '#65a30d', '#0284c7', '#a16207',
];

const CalendarPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [grounds, setGrounds] = useState([]);
  const [fields, setFields] = useState([]);
  const [filters, setFilters] = useState({
    ground_id: '',
    ground_field_id: '',
    start_date: moment().startOf('month').format('YYYY-MM-DD'),
    end_date: moment().endOf('month').format('YYYY-MM-DD'),
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState(CALENDAR_VIEWS.DAY);
  const [showSportModal, setShowSportModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [fieldSports, setFieldSports] = useState([]);
  const [selectedSport, setSelectedSport] = useState('');
  const [loadingField, setLoadingField] = useState(false);
  const [selectedFieldData, setSelectedFieldData] = useState(null);

  // Default filter values
  const defaultFilters = useMemo(() => ({
    ground_id: '',
    ground_field_id: '',
    start_date: moment().startOf('month').format('YYYY-MM-DD'),
    end_date: moment().endOf('month').format('YYYY-MM-DD'),
  }), []);

  // Check if filters have changed from defaults
  // Only check ground_id and ground_field_id since dates change automatically with view changes
  const hasFilterChanges = useMemo(() => {
    return (
      filters.ground_id !== defaultFilters.ground_id ||
      filters.ground_field_id !== defaultFilters.ground_field_id
    );
  }, [filters.ground_id, filters.ground_field_id, defaultFilters]);

  // Fetch grounds on mount
  useEffect(() => {
    const fetchGrounds = async () => {
      try {
        const response = await api.getGrounds({ 
          page_size: 1000,
          is_active: true,
          approval_status: APPROVAL_STATUS.APPROVED
        });
        setGrounds(response?.results || []);
      } catch (error) {
        console.error('Error fetching grounds:', error);
        toast.error(CALENDAR_MESSAGES.ERROR_LOAD_GROUNDS);
      }
    };
    fetchGrounds();
  }, []);

  // Fetch fields when ground is selected
  useEffect(() => {
    const fetchFields = async () => {
      if (filters.ground_id) {
        try {
          // Clear fields first to avoid showing stale data
          setFields([]);
          const response = await api.getFields({ ground: filters.ground_id, page_size: 1000 });
          setFields(response?.results || []);
        } catch (error) {
          console.error('Error fetching fields:', error);
          toast.error(CALENDAR_MESSAGES.ERROR_LOAD_FIELDS);
          setFields([]);
        }
      } else {
        setFields([]);
      }
    };
    fetchFields();
  }, [filters.ground_id]);

  // Fetch calendar data
  const fetchCalendarData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date,
      };

      if (filters.ground_id) {
        params.ground_id = filters.ground_id;
      }
      if (filters.ground_field_id) {
        params.ground_field_id = filters.ground_field_id;
      }

      const data = await api.getBookingCalendar(params);
      
      // Get current time for filtering past slots
      const now = moment();
      
      // Convert API data to calendar events and filter out past slots
      const calendarEvents = data
        .map((slot) => {
          const startDateTime = moment(`${slot.date} ${slot.start_time}`);
          const endDateTime = moment(`${slot.date} ${slot.end_time}`);
          
          return {
            id: slot.id,
            title: [slot.ground_name, slot.ground_field_name].filter(Boolean).join(' - '),
            start: startDateTime.toDate(),
            end: endDateTime.toDate(),
            resource: {
              ...slot,
            },
          };
        })
        .filter((event) => {
          // Only show events that haven't ended yet (end time is in the future)
          const endMoment = moment(event.end);
          return endMoment.isAfter(now);
        });

      setEvents(calendarEvents);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      toast.error(CALENDAR_MESSAGES.ERROR_LOAD_CALENDAR);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  const handleEventClick = async (event) => {
    if (event.resource.booking_id) {
      // If booked, navigate to booking detail
      navigate(`${routes.BOOKINGS}/${event.resource.booking_id}`);
    } else if (event.resource.status !== CALENDAR_STATUS.BOOKED) {
      // If available, show sport selection modal
      setSelectedEvent(event);
      setSelectedSport('');
      setShowSportModal(true);
      
      // Fetch field details to get sports
      if (event.resource.ground_field_id) {
        setLoadingField(true);
        try {
          const fieldData = await api.getFieldDetail(event.resource.ground_field_id);
          setSelectedFieldData(fieldData); // Store field data for later use
          if (fieldData && fieldData.sports) {
            setFieldSports(fieldData.sports);
          } else {
            setFieldSports([]);
            toast.error(CALENDAR_MODAL.NO_SPORTS);
          }
        } catch (error) {
          console.error('Error fetching field details:', error);
          toast.error(CALENDAR_MODAL.ERROR_LOAD_FIELD);
          setFieldSports([]);
          setSelectedFieldData(null);
        } finally {
          setLoadingField(false);
        }
      }
    }
  };

  const handleSportModalClose = () => {
    setShowSportModal(false);
    setSelectedEvent(null);
    setSelectedSport('');
    setFieldSports([]);
    setSelectedFieldData(null);
  };

  const handleSportContinue = () => {
    if (!selectedSport) {
      toast.error('Please select a sport');
      return;
    }

    if (!selectedEvent) {
      return;
    }

    const eventResource = selectedEvent.resource;
    // Get ground ID from eventResource or from field data
    let groundId = eventResource.ground_id;
    if (!groundId && selectedFieldData) {
      // Try to get ground from field data
      if (typeof selectedFieldData.ground === 'object' && selectedFieldData.ground?.id) {
        groundId = selectedFieldData.ground.id;
      } else if (typeof selectedFieldData.ground === 'number' || typeof selectedFieldData.ground === 'string') {
        groundId = selectedFieldData.ground;
      }
    }
    
    // Navigate to offline booking create page with pre-populated data
    navigate(routes.OFFLINE_BOOKING_CREATE, {
      state: {
        ground: groundId,
        ground_name: eventResource.ground_name,
        field: eventResource.ground_field_id,
        field_name: eventResource.ground_field_name,
        sport: selectedSport,
        date: eventResource.date,
        start_time: eventResource.start_time,
        end_time: eventResource.end_time,
      },
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      // Reset field when ground changes
      ...(key === 'ground_id' && { ground_field_id: '' }),
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      ground_id: defaultFilters.ground_id,
      ground_field_id: defaultFilters.ground_field_id,
      start_date: defaultFilters.start_date,
      end_date: defaultFilters.end_date,
    });
    setCurrentDate(new Date());
    setCurrentView(CALENDAR_VIEWS.MONTH);
  };

  const updateDateRange = useCallback((date, view) => {
    let startDate, endDate;
    const dateMoment = moment(date);
    
    if (view === CALENDAR_VIEWS.MONTH) {
      startDate = dateMoment.clone().startOf('month').format('YYYY-MM-DD');
      endDate = dateMoment.clone().endOf('month').format('YYYY-MM-DD');
    } else if (view === CALENDAR_VIEWS.WEEK) {
      startDate = dateMoment.clone().startOf('week').format('YYYY-MM-DD');
      endDate = dateMoment.clone().endOf('week').format('YYYY-MM-DD');
    } else {
      // day view
      startDate = dateMoment.format('YYYY-MM-DD');
      endDate = dateMoment.format('YYYY-MM-DD');
    }
    
    setFilters((prev) => ({
      ...prev,
      start_date: startDate,
      end_date: endDate,
    }));
  }, []);

  const handleDateChange = (date) => {
    setCurrentDate(date);
    // Update date range will be triggered by useEffect
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    // Update date range will be triggered by useEffect
  };

  // Update date range when view or currentDate changes
  useEffect(() => {
    updateDateRange(currentDate, currentView);
  }, [currentDate, currentView, updateDateRange]);

  // Unique ground fields from events with assigned colors (for legend and event styles)
  const fieldLegendItems = useMemo(() => {
    const seen = new Map();
    events.forEach((event) => {
      const id = event.resource?.ground_field_id;
      const name = event.resource?.ground_field_name || `Field ${id}`;
      const groundName = event.resource?.ground_name || '';
      if (id != null && !seen.has(id)) {
        seen.set(id, { id, name, groundName, color: FIELD_COLOR_PALETTE[seen.size % FIELD_COLOR_PALETTE.length] });
      }
    });
    return Array.from(seen.values());
  }, [events]);

  const fieldIdToColor = useMemo(() => {
    return Object.fromEntries(fieldLegendItems.map((f) => [f.id, f.color]));
  }, [fieldLegendItems]);

  const eventStyleGetter = (event) => {
    const isBooked = event.resource.status === CALENDAR_STATUS.BOOKED;
    const backgroundColor = isBooked ? '#dc3545' : (fieldIdToColor[event.resource?.ground_field_id] || '#6b7280');
    const borderColor = isBooked ? '#c82333' : backgroundColor;
    
    return {
      style: {
        backgroundColor,
        borderColor,
        color: 'white',
        borderRadius: '4px',
        border: `1px solid ${borderColor}`,
        opacity: 0.85,
      },
    };
  };

  return (
    <Container fluid className="calendar-page">
      <div className="calendar-header">
        <h2 className="calendar-title">
          <FaCalendarAlt className="me-2" />
          {CALENDAR_LABELS.TITLE}
        </h2>
      </div>

      {/* Filters */}
      <Row className="mb-2">
        <Col md={12}>
          <div className="calendar-filters">
            <Row className="g-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{CALENDAR_LABELS.GROUND}</Form.Label>
                  <Form.Select
                    value={filters.ground_id}
                    onChange={(e) => handleFilterChange('ground_id', e.target.value)}
                  >
                    <option value="">{CALENDAR_OPTIONS.ALL_GROUNDS}</option>
                    {grounds.map((ground) => (
                      <option key={ground.id} value={ground.id}>
                        {ground.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{CALENDAR_LABELS.FIELD}</Form.Label>
                  <Form.Select
                    value={filters.ground_field_id}
                    onChange={(e) => handleFilterChange('ground_field_id', e.target.value)}
                    disabled={!filters.ground_id}
                  >
                    <option value="">{CALENDAR_OPTIONS.ALL_FIELDS}</option>
                    {fields.map((field) => (
                      <option key={field.id} value={field.id}>
                        {field.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{CALENDAR_LABELS.DATE_RANGE}</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="date"
                      value={filters.start_date}
                      onChange={(e) => handleFilterChange('start_date', e.target.value)}
                    />
                    <Form.Control
                      type="date"
                      value={filters.end_date}
                      onChange={(e) => handleFilterChange('end_date', e.target.value)}
                    />
                  </div>
                </Form.Group>
              </Col>
              {hasFilterChanges && (
                  <Col md={3}>
                    <div className="d-flex justify-content-end">
                      <Button
                        variant="outline-secondary"
                        onClick={handleResetFilters}
                        className="reset-filters-btn"
                      >
                        <FaRedo />
                      </Button>
                    </div>
                  </Col>
              )}
            </Row>
          </div>
        </Col>
      </Row>
      {/* Fields and Booked legend */}
      <Row className="mb-2">
        <Col md={12}>
          <div className="calendar-legend">
            {fieldLegendItems.map((field) => (
              <span key={field.id} className="legend-item">
                <span className="legend-color" style={{ backgroundColor: field.color }} />
                <span className="legend-label">
                  {field.groundName && <strong>{field.groundName}</strong>}
                  <span className="legend-field-name">{field.name}</span>
                </span>
              </span>
            ))}
            <span className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#dc3545' }} />
              {CALENDAR_STATUS.BOOKED_LABEL}
            </span>
          </div>
        </Col>
      </Row>
      {/* Calendar */}
      <Row>
        <Col md={12}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">{CALENDAR_MESSAGES.LOADING}</span>
              </Spinner>
            </div>
          ) : (
            <div className="calendar-container">
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 600 }}
                date={currentDate}
                onSelectEvent={handleEventClick}
                onNavigate={handleDateChange}
                view={currentView}
                onView={handleViewChange}
                eventPropGetter={eventStyleGetter}
                views={[CALENDAR_VIEWS.MONTH, CALENDAR_VIEWS.WEEK, CALENDAR_VIEWS.DAY]}
                dayLayoutAlgorithm="no-overlap"
                popup
                tooltipAccessor={(event) => 
                  `${event.resource.ground_name} - ${event.resource.ground_field_name}\n` +
                  `${moment(event.start).format('h:mm A')} - ${moment(event.end).format('h:mm A')}` +
                  (event.resource.user_name ? `\n${CALENDAR_TOOLTIP.USER} ${event.resource.user_name}` : '')
                }
              />
            </div>
          )}
        </Col>
      </Row>

      {/* Sport Selection Modal */}
      <Modal
        show={showSportModal}
        onHide={handleSportModalClose}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>{CALENDAR_MODAL.TITLE}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{CALENDAR_MODAL.DESCRIPTION}</p>
          {selectedEvent && (
            <div className="mb-3">
              <p><strong>Field:</strong> {[selectedEvent.resource.ground_name, selectedEvent.resource.ground_field_name].filter(Boolean).join(' - ')}</p>
              <p><strong>Date:</strong> {moment(selectedEvent.resource.date).format('MMMM DD, YYYY')}</p>
              <p><strong>Time:</strong> {moment(selectedEvent.resource.start_time, 'HH:mm').format('h:mm A')} - {moment(selectedEvent.resource.end_time, 'HH:mm').format('h:mm A')}</p>
            </div>
          )}
          <Form.Group>
            <Form.Label>Sport <span className="text-danger">*</span></Form.Label>
            {loadingField ? (
              <div className="text-center py-3">
                <Spinner size="sm" animation="border" />
                <span className="ms-2">{CALENDAR_MODAL.LOADING_SPORTS}</span>
              </div>
            ) : (
              <SingleSelect
                options={fieldSports.map(sport => ({
                  label: sport.name || sport,
                  value: sport.id || sport,
                }))}
                value={selectedSport}
                onChange={setSelectedSport}
                placeholder="Select a sport"
                isDisabled={loadingField || fieldSports.length === 0}
              />
            )}
            {!loadingField && fieldSports.length === 0 && (
              <div className="text-danger mt-2">{CALENDAR_MODAL.NO_SPORTS}</div>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleSportModalClose}>
            {CALENDAR_MODAL.CANCEL}
          </Button>
          <Button
            variant="primary"
            onClick={handleSportContinue}
            disabled={!selectedSport || loadingField}
          >
            {CALENDAR_MODAL.CONTINUE}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CalendarPage;


